// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Box, Card, Flex, Grid, Menu, Select, Stack, Text, TextInput } from '@mantine/core';
import { HTTP_HL7_ORG } from '@medplum/core';
import type { ChargeItem, CodeableConcept, Money } from '@medplum/fhirtypes';
import { CodeableConceptInput, useMedplum } from '@medplum/react';
import { IconTrash } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { applyChargeItemDefinition, findActiveChargeItemDefinitionByCpt } from '../../utils/chargeitems';
import type { EmCptOptions } from '../../utils/em-cpt';
import { showErrorNotification } from '../../utils/notifications';

const CHARGE_ITEM_MODIFIER_URL = `${HTTP_HL7_ORG}/fhir/StructureDefinition/chargeitem-modifier`;
const CPT_CODE_SYSTEM = 'http://www.ama-assn.org/go/cpt';

interface ChargeItemPanelProps {
  chargeItem: ChargeItem;
  onChange: (chargeItem: ChargeItem) => void;
  onDelete: (chargeItem: ChargeItem) => void;
  readOnly?: boolean;
  emCptOptions?: EmCptOptions;
}

export default function ChargeItemPanel(props: ChargeItemPanelProps): JSX.Element {
  const { chargeItem, onChange, onDelete, readOnly = false, emCptOptions } = props;
  const medplum = useMedplum();
  const [modifierExtensionValue, setModifierExtensionValue] = useState<CodeableConcept | undefined>();
  const [cptCodes, setCptCodes] = useState<CodeableConcept>();
  const [price, setPrice] = useState<Money | undefined>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: getModifierExtension is a stable pure function, chargeItem already in deps
  useEffect(() => {
    setPrice(chargeItem.priceOverride);
    setModifierExtensionValue(getModifierExtension(chargeItem));
    const cptCodes = chargeItem?.code?.coding?.filter((coding) => coding.system === CPT_CODE_SYSTEM) ?? [];
    setCptCodes({ coding: cptCodes });
  }, [chargeItem]);

  const updateCptCode = async (value: CodeableConcept | undefined): Promise<void> => {
    const selectedCode = value?.coding?.[0]?.code;
    const definition = await findActiveChargeItemDefinitionByCpt(medplum, selectedCode);

    const otherCodings = chargeItem.code?.coding?.filter((c) => c.system !== CPT_CODE_SYSTEM) ?? [];
    const cptCodings = value?.coding ?? [];
    const updatedChargeItem: ChargeItem = {
      ...chargeItem,
      code: { ...chargeItem.code, coding: [...otherCodings, ...cptCodings] },
      definitionCanonical: definition?.url ? [definition.url] : undefined,
      ...(selectedCode && !definition ? { priceOverride: undefined } : {}),
    };
    if (updatedChargeItem.id) {
      await updateChargeItem(updatedChargeItem);
    } else {
      onChange(updatedChargeItem);
    }
  };

  const updateModifiers = async (value: CodeableConcept | undefined): Promise<void> => {
    if (!value) {
      const updatedChargeItem = { ...chargeItem };
      updatedChargeItem.extension = undefined;
      await updateChargeItem(updatedChargeItem);
      return;
    }
    const updatedChargeItem = { ...chargeItem };
    updatedChargeItem.extension = updatedChargeItem.extension ? [...updatedChargeItem.extension] : [];
    const modifierExtension = {
      url: CHARGE_ITEM_MODIFIER_URL,
      valueCodeableConcept: value,
    };
    const existingIndex = updatedChargeItem.extension.findIndex((ext) => ext.url === CHARGE_ITEM_MODIFIER_URL);
    if (existingIndex >= 0) {
      updatedChargeItem.extension[existingIndex] = modifierExtension;
    } else {
      updatedChargeItem.extension.push(modifierExtension);
    }
    await updateChargeItem(updatedChargeItem);
  };

  const updateChargeItem = async (updatedChargeItem: ChargeItem): Promise<void> => {
    await medplum.updateResource(updatedChargeItem);
    const appliedChargeItem = await applyChargeItemDefinition(medplum, updatedChargeItem);
    onChange(appliedChargeItem);
  };

  const deleteChargeItem = async (): Promise<void> => {
    await medplum.deleteResource('ChargeItem', chargeItem.id as string);
    onDelete(chargeItem);
  };

  const getModifierExtension = (item: ChargeItem): CodeableConcept | undefined => {
    if (item?.extension) {
      const modifierExtension = item.extension.find((ext) => ext.url === CHARGE_ITEM_MODIFIER_URL);
      return modifierExtension?.valueCodeableConcept;
    }
    return undefined;
  };

  const cptCodeKey = `cpt-${chargeItem.id}-${JSON.stringify(cptCodes?.coding)}`;
  const modifierKey = `modifier-${chargeItem.id}-${JSON.stringify(modifierExtensionValue)}`;
  const selectedCpt = cptCodes?.coding?.[0]?.code ?? '';
  const cptSelectData = emCptOptions
    ? [
        ...emCptOptions.codes.map((code) => ({ value: code, label: code })),
        ...(selectedCpt && !emCptOptions.codes.includes(selectedCpt)
          ? [{ value: selectedCpt, label: selectedCpt, disabled: true }]
          : []),
      ]
    : undefined;

  return (
    <Card withBorder shadow="sm" p={0}>
      <Stack gap="xs" p="md">
        <Flex justify="space-between" align="flex-start">
          <Box flex={1} mr="md" maw="calc(100% - 60px)">
            {cptSelectData ? (
              <Select
                key={cptCodeKey}
                label="CPT Code"
                data={cptSelectData}
                value={selectedCpt || null}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(value) =>
                  updateCptCode(value ? { coding: [{ system: CPT_CODE_SYSTEM, code: value }] } : undefined)
                }
              />
            ) : (
              <CodeableConceptInput
                key={cptCodeKey}
                binding="http://www.ama-assn.org/go/cpt/vs"
                label="CPT Code"
                name="cptCode"
                path="cptCode"
                defaultValue={cptCodes}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={readOnly ? undefined : updateCptCode}
              />
            )}
          </Box>
          {!readOnly && (
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle">
                  <IconTrash size={24} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={deleteChargeItem}>
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Flex>

        <CodeableConceptInput
          key={modifierKey}
          binding="http://hl7.org/fhir/ValueSet/claim-modifiers"
          label="Modifiers"
          name="modifiers"
          path="modifiers"
          defaultValue={modifierExtensionValue}
          onChange={readOnly ? undefined : updateModifiers}
          disabled={readOnly}
        />

        <Grid columns={12} mt="md">
          <Grid.Col span={7}>
            <Flex h="100%" direction="column" justify="flex-end" pt={4}>
              <Text size="sm" c="dimmed">
                Price calculated from Price chart, taking into account applied modifiers and patient's selected
                insurance plan.
              </Text>
            </Flex>
          </Grid.Col>
          <Grid.Col span={5}>
            <Text size="sm" fw={500} mb={8}>
              Calculated Price
            </Text>
            <TextInput value={price?.value ? `$${price.value.toFixed(2)}` : 'N/A'} readOnly />
          </Grid.Col>
        </Grid>
      </Stack>
    </Card>
  );
}
